import type { CommunicationMetric } from "@/shared/api/communication-stats"
import { CommunicationMetricChart } from "./communication-metric-chart"
import type { MetricPresentation } from "./metric-presentations"

export const MONTH_PERIOD_LABEL = "Последние 30 полных суток по Москве; текущие сутки не входят"

interface CommunicationMetricCardProps {
    metric: CommunicationMetric
    presentation: MetricPresentation
}

const formatCount = (value: number) => new Intl.NumberFormat("ru-RU").format(value)

export const CommunicationMetricCard = ({ metric, presentation }: CommunicationMetricCardProps) => {
    const headingId = `communication-metric-${presentation.key}`

    if (metric.status === "vcs_not_connected") {
        return (
            <article className="box min-w-0" aria-labelledby={headingId} data-metric={presentation.key}>
                <h3 id={headingId} className="text-lg font-semibold text-color-dark">{presentation.title}</h3>
                <p className="mt-4" role="status">Видеосвязь не подключена сообществу</p>
                <div className="mt-5 h-72 w-full min-w-0 overflow-hidden" aria-label={`График недоступен: ${presentation.title}`}>
                    <p>График не строится: ряд отсутствует</p>
                </div>
            </article>
        )
    }

    if (metric.status === "unavailable") {
        return (
            <article className="box min-w-0" aria-labelledby={headingId} data-metric={presentation.key}>
                <h3 id={headingId} className="text-lg font-semibold text-color-dark">{presentation.title}</h3>
                <div className="mt-4" role="alert">
                    <p>Источник недоступен</p>
                    {metric.reason && <p>{metric.reason}</p>}
                </div>
                <div className="mt-5 h-72 w-full min-w-0 overflow-hidden" aria-label={`График недоступен: ${presentation.title}`}>
                    <p>График не строится: ряд отсутствует</p>
                </div>
            </article>
        )
    }

    if (metric.status !== "ok" && metric.status !== "sip_disabled") {
        return null
    }

    const hasNoEvents = metric.last30Days === 0

    return (
        <article className="box min-w-0" aria-labelledby={headingId} data-metric={presentation.key}>
            <h3 id={headingId} className="text-lg font-semibold text-color-dark">{presentation.title}</h3>
            {metric.status === "sip_disabled" && (
                <p className="mt-3" role="status">Телефония не подключена в контуре</p>
            )}
            {hasNoEvents && <p className="mt-3" role="status">Событий не было</p>}
            <dl className="mt-4">
                <div>
                    <dt className="text-sm text-gray-600">{presentation.totalDescription}</dt>
                    <dd className="text-3xl font-semibold text-color-dark">
                        {formatCount(metric.total)} <span className="text-sm font-normal">{presentation.unit}</span>
                    </dd>
                </div>
                <div className="mt-4">
                    <dt className="text-sm text-gray-600">{MONTH_PERIOD_LABEL}</dt>
                    <dd className="text-xl font-medium text-color-dark">
                        {formatCount(metric.last30Days)} <span className="text-sm font-normal">{presentation.unit}</span>
                    </dd>
                </div>
            </dl>
            <CommunicationMetricChart metric={metric} presentation={presentation} />
        </article>
    )
}
